import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  BadgePercent, Plus, Trash2, CheckCircle, Search, Copy, Users, 
  DollarSign, ShieldCheck, RefreshCw, X, Filter, Calendar, MapPin, 
  ChevronDown, ChevronUp, Download, Eye, ExternalLink, Briefcase, 
  CreditCard, Award, Phone, Mail, FileText, CheckCircle2, AlertCircle
} from 'lucide-react';
import { useAuth } from '../components/AuthGuard';

const InputBlock = ({ label, type = 'text', value, onChange, placeholder, required = false, disabled = false }) => (
  <div>
    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1 pl-1">
      {label} {required && <span className="text-[#8B1A2D]">*</span>}
    </label>
    <input
      type={type}
      required={required}
      disabled={disabled}
      className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-[#8B1A2D] focus:ring-1 focus:ring-[#8B1A2D]/30 transition-all font-medium placeholder-zinc-600 disabled:opacity-50"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  </div>
);

const SelectBlock = ({ label, value, onChange, options, required = false }) => (
  <div>
    <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1 pl-1">
      {label} {required && <span className="text-[#8B1A2D]">*</span>}
    </label>
    <select
      required={required}
      className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-[#8B1A2D] focus:ring-1 focus:ring-[#8B1A2D]/30 transition-all font-medium"
      value={value}
      onChange={onChange}
    >
      {options.map((opt, i) => (
        <option key={i} value={typeof opt === 'string' ? opt : opt.value}>
          {typeof opt === 'string' ? opt : opt.label}
        </option>
      ))}
    </select>
  </div>
);

const FormSectionHeader = ({ step, title, subtitle }) => (
  <div className="flex items-center gap-3 pb-2 border-b border-zinc-800/80 mb-4">
    <span className="w-6 h-6 rounded-full bg-[#8B1A2D]/20 text-[#8B1A2D] border border-[#8B1A2D]/40 text-[10px] font-black flex items-center justify-center">
      {step}
    </span>
    <div>
      <h3 className="text-xs font-black uppercase tracking-wider text-white">{title}</h3>
      {subtitle && <p className="text-[10px] text-zinc-500 font-medium">{subtitle}</p>}
    </div>
  </div>
);

// --- Comprehensive Agent Ledger & Dossier Slide-Over ---
const AgentCMSPanel = ({ agent, students, onClose }) => {
  const [activeTab, setActiveTab] = useState('ledger'); // 'ledger' | 'profile' | 'payouts'
  const [sortBy, setSortBy] = useState('date_desc');
  const [filterStatus, setFilterStatus] = useState('all');

  const processedStudents = useMemo(() => {
    let result = [...students];

    if (filterStatus !== 'all') {
      result = result.filter(s => {
        const status = s.status || s.paymentStatus || 'unknown';
        return status.toLowerCase() === filterStatus.toLowerCase();
      });
    }

    result.sort((a, b) => {
      const nameA = ((a.firstName || '') + ' ' + (a.lastName || '')).toLowerCase();
      const nameB = ((b.firstName || '') + ' ' + (b.lastName || '')).toLowerCase();
      const dateA = new Date(a.createdAt || a.startedAt || 0).getTime();
      const dateB = new Date(b.createdAt || b.startedAt || 0).getTime();

      switch (sortBy) {
        case 'name_asc': return nameA.localeCompare(nameB);
        case 'name_desc': return nameB.localeCompare(nameA);
        case 'date_desc': return dateB - dateA;
        case 'date_asc': return dateA - dateB;
        default: return 0;
      }
    });

    return result;
  }, [students, sortBy, filterStatus]);

  if (!agent) return null;

  const totalGrossSales = students.length * 25000;
  const commissionEarned = (totalGrossSales * (parseFloat(agent.commissionRate) || 10)) / 100;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] transition-opacity animate-in fade-in"
        onClick={onClose}
      />
      
      <div className="fixed inset-y-0 right-0 w-full md:w-[650px] lg:w-[850px] bg-zinc-950 border-l border-zinc-800 shadow-2xl z-[101] flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex-none p-6 border-b border-zinc-800 bg-zinc-900/60">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                  Portal 04 &bull; Sales Rep Dossier
                </span>
                <span className="text-xs font-mono font-bold text-amber-400 bg-zinc-900 px-2 py-0.5 rounded border border-zinc-800">
                  {agent.repCode}
                </span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${
                  agent.status === 'active' ? 'bg-emerald-950/80 text-emerald-400 border border-emerald-800' : 'bg-zinc-800 text-zinc-400'
                }`}>
                  {agent.status || 'active'}
                </span>
              </div>
              <h2 className="text-2xl font-black text-white">{agent.name}</h2>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-zinc-400">
                <span className="flex items-center gap-1.5"><Users size={14} className="text-amber-400" /> {students.length} Enrolled Scholars</span>
                <span className="flex items-center gap-1.5"><BadgePercent size={14} className="text-emerald-400" /> {agent.commissionRate || 10}% Commission</span>
                <span className="flex items-center gap-1.5"><DollarSign size={14} className="text-amber-400" /> KES {commissionEarned.toLocaleString()} Accrued</span>
                <span className="flex items-center gap-1.5"><MapPin size={14} className="text-zinc-500" /> {agent.primaryTerritory || agent.county || 'Unassigned'}</span>
              </div>
            </div>
            <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors">
              <X size={18} />
            </button>
          </div>

          {/* Navigation Tabs */}
          <div className="flex gap-2 mt-6">
            <button
              onClick={() => setActiveTab('ledger')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                activeTab === 'ledger' ? 'bg-[#8B1A2D] text-white shadow-lg shadow-[#8B1A2D]/20' : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
              }`}
            >
              Enrolled Scholars Ledger ({students.length})
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                activeTab === 'profile' ? 'bg-[#8B1A2D] text-white shadow-lg shadow-[#8B1A2D]/20' : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
              }`}
            >
              Agent Profile &amp; KYC
            </button>
            <button
              onClick={() => setActiveTab('payouts')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                activeTab === 'payouts' ? 'bg-[#8B1A2D] text-white shadow-lg shadow-[#8B1A2D]/20' : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
              }`}
            >
              Settlement &amp; Banking
            </button>
          </div>
        </div>

        {/* Panel Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {activeTab === 'ledger' && (
            <div className="space-y-4">
              {/* Filter controls */}
              <div className="flex items-center justify-between gap-3 bg-zinc-900/60 p-3 rounded-xl border border-zinc-800">
                <div className="flex items-center gap-2">
                  <Filter size={14} className="text-zinc-500" />
                  <select 
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="bg-zinc-950 border border-zinc-800 text-white text-xs rounded-lg px-2.5 py-1.5 focus:outline-none"
                  >
                    <option value="all">All Payment Statuses</option>
                    <option value="paid">Tuition Paid</option>
                    <option value="pending">Tuition Pending</option>
                    <option value="verified">Verified Admission</option>
                  </select>
                </div>
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-zinc-950 border border-zinc-800 text-white text-xs rounded-lg px-2.5 py-1.5 focus:outline-none"
                >
                  <option value="date_desc">Newest Admissions First</option>
                  <option value="date_asc">Oldest Admissions First</option>
                  <option value="name_asc">Name (A-Z)</option>
                </select>
              </div>

              {processedStudents.length === 0 ? (
                <div className="py-16 text-center text-zinc-500 font-bold text-xs uppercase tracking-widest bg-zinc-900/30 rounded-2xl border border-zinc-900">
                  No scholars currently registered under Rep Code {agent.repCode}.
                </div>
              ) : (
                <div className="space-y-3">
                  {processedStudents.map((st, i) => (
                    <div key={st.id || i} className="p-4 bg-zinc-900/50 border border-zinc-800/80 rounded-2xl hover:border-zinc-700 transition-all">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-white text-sm">{st.firstName} {st.lastName}</span>
                            <span className="text-[10px] font-mono px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded">
                              {st.studentId || st.id}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-zinc-400">
                            <span>{st.email || 'No email'}</span>
                            <span>&bull;</span>
                            <span>{st.phone || 'No phone'}</span>
                            <span>&bull;</span>
                            <span>{st.track || st.course || 'DIS Tech Track'}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`px-2.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider ${
                            st.paymentStatus === 'paid' ? 'bg-emerald-950 text-emerald-400 border border-emerald-800' : 'bg-amber-950 text-amber-400 border border-amber-800'
                          }`}>
                            {st.paymentStatus || st.status || 'Pending'}
                          </span>
                          <p className="text-[10px] font-mono text-zinc-500 mt-1">
                            {st.createdAt ? new Date(st.createdAt).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Government National ID</div>
                  <div className="text-sm font-mono font-bold text-white mt-1">{agent.nationalId || 'Not provided'}</div>
                </div>
                <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Gender / Country</div>
                  <div className="text-sm font-bold text-white mt-1">{agent.gender || 'N/A'} &bull; {agent.country || 'Kenya'}</div>
                </div>
                <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Education Level</div>
                  <div className="text-sm font-bold text-white mt-1">{agent.educationLevel || 'Not specified'}</div>
                </div>
                <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Current Occupation</div>
                  <div className="text-sm font-bold text-white mt-1">{agent.currentOccupation || 'Field Representative'}</div>
                </div>
                <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Sales Experience</div>
                  <div className="text-sm font-bold text-white mt-1">{agent.salesExperience || '1-2 years'}</div>
                </div>
                <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Network Reach Capacity</div>
                  <div className="text-sm font-bold text-white mt-1">{agent.networkSize || '50-200 contacts'}</div>
                </div>
              </div>

              <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800 space-y-2">
                <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Territory &amp; Methodologies</div>
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div>
                    <span className="text-xs text-zinc-400">Primary Territory:</span>
                    <p className="text-sm font-bold text-white">{agent.primaryTerritory || agent.county || 'All Counties'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-zinc-400">Secondary Counties:</span>
                    <p className="text-sm font-bold text-white">{agent.secondaryTerritories || 'None'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-zinc-400">Dominant Outreach Channel:</span>
                    <p className="text-sm font-bold text-white">{agent.operatingChannels || 'Direct Field Visits'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-zinc-400">Monthly Enrollment Target:</span>
                    <p className="text-sm font-bold text-emerald-400">{agent.monthlyEnrollmentTarget || '6-15 students / month'}</p>
                  </div>
                </div>
              </div>

              {agent.bio && (
                <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500 mb-1">Agent Elevator Pitch &amp; Strategy</div>
                  <p className="text-xs text-zinc-300 leading-relaxed">{agent.bio}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'payouts' && (
            <div className="space-y-6">
              <div className="p-6 bg-gradient-to-br from-emerald-950/40 via-zinc-900 to-black border border-emerald-900/50 rounded-2xl">
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400">Settlement Account Summary</div>
                <div className="grid grid-cols-2 gap-6 mt-4">
                  <div>
                    <span className="text-xs text-zinc-400">M-Pesa Payout Number:</span>
                    <div className="text-lg font-mono font-black text-white">{agent.payoutMpesa || agent.phone || 'Not Set'}</div>
                  </div>
                  <div>
                    <span className="text-xs text-zinc-400">Disbursement Cycle:</span>
                    <div className="text-lg font-bold text-emerald-400">{agent.payoutFrequency || 'Weekly (Every Friday)'}</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Bank Name</div>
                  <div className="text-sm font-bold text-white mt-1">{agent.bankName || 'Direct M-Pesa Payout'}</div>
                </div>
                <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Account Number</div>
                  <div className="text-sm font-mono font-bold text-white mt-1">{agent.bankAccount || 'N/A'}</div>
                </div>
                <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">KRA PIN</div>
                  <div className="text-sm font-mono font-bold text-white mt-1">{agent.kraPin || 'Exempt / Not Provided'}</div>
                </div>
                <div className="p-4 bg-zinc-900/60 rounded-2xl border border-zinc-800">
                  <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">KES 1,000 Activation Ref</div>
                  <div className="text-sm font-mono font-bold text-amber-400 mt-1">{agent.activationRef || agent.mpesaReceipt || 'ADMIN-WAIVED'}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default function SalesRepsManager() {
  const { user } = useAuth();
  const [reps, setReps] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Filters
  const [territoryFilter, setTerritoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [frequencyFilter, setFrequencyFilter] = useState('all');

  // Form State (100% Onboarding Schema)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [gender, setGender] = useState('Male');
  const [country, setCountry] = useState('Kenya');
  const [county, setCounty] = useState('');
  const [bio, setBio] = useState('');

  const [educationLevel, setEducationLevel] = useState("Bachelor's Degree");
  const [currentOccupation, setCurrentOccupation] = useState('Full-time Field Sales');
  const [salesExperience, setSalesExperience] = useState('1-2 years');
  const [productsSold, setProductsSold] = useState('');
  const [networkSize, setNetworkSize] = useState('200-500 contacts');
  const [hasTransport, setHasTransport] = useState('Motorcycle (Boda Boda)');

  const [primaryTerritory, setPrimaryTerritory] = useState('');
  const [secondaryTerritories, setSecondaryTerritories] = useState('');
  const [targetAudience, setTargetAudience] = useState('Secondary School Leavers & TVET');
  const [operatingChannels, setOperatingChannels] = useState('Door-to-Door & Direct Field Visits');
  const [monthlyEnrollmentTarget, setMonthlyEnrollmentTarget] = useState('6-15 students (KES 6,000 - 15,000 commission)');
  const [referredByAgent, setReferredByAgent] = useState('');

  const [customCode, setCustomCode] = useState('');
  const [commissionRate, setCommissionRate] = useState('10');
  const [payoutMpesa, setPayoutMpesa] = useState('');
  const [payoutFrequency, setPayoutFrequency] = useState('Weekly (Every Friday)');
  const [bankName, setBankName] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [kraPin, setKraPin] = useState('');

  const [docRepIdUrl, setDocRepIdUrl] = useState('');
  const [docHeadshotUrl, setDocHeadshotUrl] = useState('');
  const [activationRef, setActivationRef] = useState('');
  const [repStatus, setRepStatus] = useState('active');

  const fetchRepsAndStudents = async () => {
    setLoading(true);
    try {
      const repSnap = await getDocs(collection(db, 'sales_reps'));
      const repList = [];
      repSnap.forEach(d => repList.push({ id: d.id, ...d.data() }));

      const studentSnap = await getDocs(collection(db, 'students'));
      const studentList = [];
      studentSnap.forEach(d => studentList.push({ id: d.id, ...d.data() }));

      setStudents(studentList);
      setReps(repList);
    } catch (e) {
      console.error('Error fetching sales reps:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRepsAndStudents();
  }, []);

  const generateRepCode = (nameStr) => {
    const cleanName = (nameStr || 'REP').replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 5) || 'REP';
    const num = Math.floor(100 + Math.random() * 900);
    return `DIS-${cleanName}${num}`;
  };

  const handleAddRep = async (e) => {
    e.preventDefault();
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    if (!fullName) {
      alert('First and Last Name are required.');
      return;
    }

    setStatusMessage('saving');
    const finalCode = (customCode.trim() || generateRepCode(firstName || fullName)).toUpperCase();

    try {
      await setDoc(doc(db, 'sales_reps', finalCode), {
        repCode: finalCode,
        name: fullName,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim(),
        nationalId: nationalId.trim(),
        gender,
        country: country.trim() || 'Kenya',
        county: county.trim(),
        bio: bio.trim(),

        educationLevel,
        currentOccupation,
        salesExperience,
        productsSold: productsSold.trim(),
        networkSize,
        hasTransport,

        primaryTerritory: primaryTerritory.trim() || county.trim(),
        secondaryTerritories: secondaryTerritories.trim(),
        targetAudience,
        operatingChannels,
        monthlyEnrollmentTarget,
        referredByAgent: referredByAgent.trim(),

        commissionRate: parseFloat(commissionRate) || 10,
        payoutMpesa: payoutMpesa.trim() || phone.trim(),
        payoutFrequency,
        bankName: bankName.trim(),
        bankAccount: bankAccount.trim(),
        kraPin: kraPin.trim(),

        docRepIdUrl: docRepIdUrl.trim(),
        docHeadshotUrl: docHeadshotUrl.trim(),
        activationRef: activationRef.trim() || 'ADMIN-ISSUED',
        status: repStatus,

        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        createdBy: user?.email || 'Executive Admin'
      }, { merge: true });

      // Reset form
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setNationalId('');
      setCounty('');
      setBio('');
      setProductsSold('');
      setPrimaryTerritory('');
      setSecondaryTerritories('');
      setCustomCode('');
      setCommissionRate('10');
      setPayoutMpesa('');
      setBankName('');
      setBankAccount('');
      setKraPin('');
      setDocRepIdUrl('');
      setDocHeadshotUrl('');
      setActivationRef('');

      setStatusMessage('success');
      fetchRepsAndStudents();
      setTimeout(() => {
        setStatusMessage('');
        setIsFormOpen(false);
      }, 2500);
    } catch (err) {
      console.error('Error adding sales rep:', err);
      setStatusMessage('error');
      alert('Failed to register Sales Representative.');
    }
  };

  const handleDeleteRep = async (repCode) => {
    if (!window.confirm(`Revoke credentials and remove Sales Rep ${repCode}?`)) return;
    try {
      await deleteDoc(doc(db, 'sales_reps', repCode));
      fetchRepsAndStudents();
    } catch (err) {
      console.error('Error deleting rep:', err);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert(`Copied Rep Code: ${text}`);
  };

  const getRepMetrics = (code) => {
    const matchedStudents = students.filter(s => 
      (s.salesRepCode || '').toUpperCase() === (code || '').toUpperCase()
    );
    const totalStudents = matchedStudents.length;
    const totalGrossSales = totalStudents * 25000;
    return { matchedStudents, totalStudents, totalGrossSales };
  };

  // Distinct territories for filter
  const territories = useMemo(() => {
    const set = new Set();
    reps.forEach(r => {
      const t = r.primaryTerritory || r.county;
      if (t) set.add(t);
    });
    return Array.from(set).sort();
  }, [reps]);

  // Filtering
  const filteredReps = useMemo(() => {
    return reps.filter(r => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = 
        (r.name || '').toLowerCase().includes(q) ||
        (r.repCode || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.phone || '').toLowerCase().includes(q) ||
        (r.nationalId || '').toLowerCase().includes(q) ||
        (r.primaryTerritory || r.county || '').toLowerCase().includes(q);

      const matchesTerritory = territoryFilter === 'all' || (r.primaryTerritory || r.county) === territoryFilter;
      const matchesStatus = statusFilter === 'all' || (r.status || 'active') === statusFilter;
      const matchesFrequency = frequencyFilter === 'all' || (r.payoutFrequency || '').toLowerCase().includes(frequencyFilter.toLowerCase());

      return matchesSearch && matchesTerritory && matchesStatus && matchesFrequency;
    });
  }, [reps, searchQuery, territoryFilter, statusFilter, frequencyFilter]);

  // CSV Export
  const exportToCSV = () => {
    const headers = [
      'Rep Code', 'Agent Name', 'Email', 'Phone', 'National ID', 'County / Territory',
      'Occupation', 'Commission Rate (%)', 'Students Enrolled', 'Est Gross KES',
      'Est Commission KES', 'Payout Frequency', 'Payout M-Pesa', 'Activation Ref', 'Status'
    ];
    const rows = filteredReps.map(r => {
      const m = getRepMetrics(r.repCode);
      const earned = (m.totalGrossSales * (r.commissionRate || 10)) / 100;
      return [
        r.repCode,
        `"${r.name || ''}"`,
        r.email || '',
        r.phone || '',
        r.nationalId || '',
        `"${r.primaryTerritory || r.county || ''}"`,
        `"${r.currentOccupation || ''}"`,
        r.commissionRate || 10,
        m.totalStudents,
        m.totalGrossSales,
        earned,
        `"${r.payoutFrequency || 'Weekly'}"`,
        r.payoutMpesa || r.phone || '',
        r.activationRef || '',
        r.status || 'active'
      ];
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `DIS_Sales_Reps_Ledger_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Executive Header Banner */}
      <div className="bg-gradient-to-r from-zinc-950 via-zinc-900 to-black border border-zinc-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#8B1A2D]/10 blur-3xl rounded-full pointer-events-none -mr-20 -mt-20" />
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="p-2.5 bg-[#8B1A2D]/20 text-[#8B1A2D] border border-[#8B1A2D]/30 rounded-2xl">
                <BadgePercent size={22} />
              </span>
              <span className="px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                Portal 04 &bull; Commercial Sales &amp; Commission Ledger
              </span>
            </div>
            <h1 className="text-3xl font-black text-white uppercase tracking-tight">
              Onboarding Sales Agent Master Registry
            </h1>
            <p className="text-zinc-400 text-xs font-bold max-w-2xl leading-relaxed">
              Register commercial field representatives, student campus ambassadors, and mobilization agents. Issue unique Rep Codes, configure automated M-Pesa payouts, and audit commission settlements in real-time.
            </p>
          </div>

          <div className="flex items-center gap-4 bg-zinc-900/90 border border-zinc-800 p-4 rounded-2xl">
            <div className="text-right">
              <div className="text-[10px] font-black text-zinc-400 uppercase tracking-wider">Active Field Agents</div>
              <div className="text-2xl font-black text-[#8B1A2D]">
                {reps.length} <span className="text-xs text-zinc-500 font-bold">Agents</span>
              </div>
            </div>
            <button
              onClick={() => setIsFormOpen(!isFormOpen)}
              className="flex items-center gap-2 px-5 py-3 bg-[#8B1A2D] hover:bg-[#a61f37] text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-[#8B1A2D]/20 cursor-pointer"
            >
              <Plus size={15} /> {isFormOpen ? 'Close Form' : 'Register New Agent'}
            </button>
          </div>
        </div>
      </div>

      {/* Collapsible Full Onboarding Form */}
      {isFormOpen && (
        <div className="bg-zinc-950 border border-zinc-800 rounded-3xl p-6 shadow-2xl space-y-6 animate-in slide-in-from-top-4 duration-300">
          <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
            <div>
              <h2 className="text-base font-black text-white uppercase tracking-wider flex items-center gap-2">
                <Plus size={18} className="text-[#8B1A2D]" /> Register New Sales Representative
              </h2>
              <p className="text-xs text-zinc-500">All 7 onboarding steps mapped directly to executive Firestore schema</p>
            </div>
            <button 
              onClick={() => setIsFormOpen(false)}
              className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl"
            >
              <X size={16} />
            </button>
          </div>

          <form onSubmit={handleAddRep} className="space-y-6">
            {/* Step 1: Identity & KYC */}
            <div>
              <FormSectionHeader 
                step="1" 
                title="Personal Identity &amp; Contact Credentials" 
                subtitle="Government identity details and primary communication channels"
              />
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <InputBlock label="First Name" placeholder="e.g. James" value={firstName} onChange={e => setFirstName(e.target.value)} required />
                <InputBlock label="Last Name" placeholder="e.g. Omondi" value={lastName} onChange={e => setLastName(e.target.value)} required />
                <InputBlock label="Email Address" type="email" placeholder="agent@dis.ac.ke" value={email} onChange={e => setEmail(e.target.value)} required />
                <InputBlock label="Primary Phone (M-Pesa)" type="tel" placeholder="+254 7XX XXX XXX" value={phone} onChange={e => setPhone(e.target.value)} required />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <InputBlock label="National ID / Passport Number" placeholder="e.g. 30192837" value={nationalId} onChange={e => setNationalId(e.target.value)} required />
                <SelectBlock 
                  label="Gender" 
                  value={gender} 
                  onChange={e => setGender(e.target.value)} 
                  options={['Male', 'Female', 'Prefer not to say']} 
                  required 
                />
                <InputBlock label="Country of Operation" placeholder="Kenya" value={country} onChange={e => setCountry(e.target.value)} required />
                <InputBlock label="Operating County / City" placeholder="e.g. Nakuru / Baringo" value={county} onChange={e => setCounty(e.target.value)} required />
              </div>
              <div className="mt-4">
                <label className="block text-[9px] font-black uppercase tracking-widest text-zinc-400 mb-1 pl-1">
                  Agent Sales Elevator Pitch &amp; Mobilization Strategy
                </label>
                <textarea
                  value={bio}
                  onChange={e => setBio(e.target.value)}
                  placeholder="Describe your sales style, mobilization background, and target networks..."
                  rows={2}
                  className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-[#8B1A2D] placeholder-zinc-600 font-medium"
                />
              </div>
            </div>

            {/* Step 2: Experience & Reach */}
            <div>
              <FormSectionHeader 
                step="2" 
                title="Sales &amp; Recruitment Track Record" 
                subtitle="Commercial history, academic background, and reachable contact network"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SelectBlock
                  label="Highest Qualification"
                  value={educationLevel}
                  onChange={e => setEducationLevel(e.target.value)}
                  options={[
                    "Secondary School (KCSE)",
                    "Certificate / Diploma",
                    "Bachelor's Degree",
                    "Postgraduate"
                  ]}
                  required
                />
                <SelectBlock
                  label="Current Occupation Status"
                  value={currentOccupation}
                  onChange={e => setCurrentOccupation(e.target.value)}
                  options={[
                    "Full-time Field Sales",
                    "Independent Consultant",
                    "Employed (Part-time Sales)",
                    "Campus Student Leader",
                    "Community Mobilizer"
                  ]}
                  required
                />
                <SelectBlock
                  label="Years in Sales / Recruitment"
                  value={salesExperience}
                  onChange={e => setSalesExperience(e.target.value)}
                  options={[
                    "Under 1 year",
                    "1-2 years",
                    "3-5 years",
                    "5+ years (Senior Field Rep)"
                  ]}
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <InputBlock label="Previous Products / Services Sold" placeholder="e.g. TVET Courses, Insurance, Banking" value={productsSold} onChange={e => setProductsSold(e.target.value)} />
                <SelectBlock
                  label="Reachable Contact Network"
                  value={networkSize}
                  onChange={e => setNetworkSize(e.target.value)}
                  options={[
                    "Under 50 contacts",
                    "50-200 contacts",
                    "200-500 contacts",
                    "500+ contacts (Institutions / Churches)"
                  ]}
                  required
                />
                <SelectBlock
                  label="Personal Transport Available"
                  value={hasTransport}
                  onChange={e => setHasTransport(e.target.value)}
                  options={[
                    "Public Transit / Walking",
                    "Motorcycle (Boda Boda)",
                    "Personal Car"
                  ]}
                />
              </div>
            </div>

            {/* Step 3: Territory & Channels */}
            <div>
              <FormSectionHeader 
                step="3" 
                title="Primary Territory &amp; Outreach Methodology" 
                subtitle="Operating regions, targeted demographics, and monthly intake goals"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InputBlock label="Primary Territory" placeholder="e.g. Nakuru County" value={primaryTerritory} onChange={e => setPrimaryTerritory(e.target.value)} />
                <InputBlock label="Secondary Adjacent Counties" placeholder="e.g. Baringo, Kericho, Bomet" value={secondaryTerritories} onChange={e => setSecondaryTerritories(e.target.value)} />
                <SelectBlock
                  label="Target Audience Focus"
                  value={targetAudience}
                  onChange={e => setTargetAudience(e.target.value)}
                  options={[
                    "Secondary School Leavers & TVET",
                    "University Graduates seeking Tech Skills",
                    "Working Professionals (Evening/Weekend)",
                    "Churches & Youth Community Groups",
                    "All Academic Categories"
                  ]}
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <SelectBlock
                  label="Primary Outreach Channel"
                  value={operatingChannels}
                  onChange={e => setOperatingChannels(e.target.value)}
                  options={[
                    "Door-to-Door & Direct Field Visits",
                    "WhatsApp Community Groups & Social Media",
                    "Campus & College Activations",
                    "Church & Civic Youth Seminars"
                  ]}
                  required
                />
                <SelectBlock
                  label="Monthly Enrollment Target"
                  value={monthlyEnrollmentTarget}
                  onChange={e => setMonthlyEnrollmentTarget(e.target.value)}
                  options={[
                    "3-5 students (KES 3,000 - 5,000 commission)",
                    "6-15 students (KES 6,000 - 15,000 commission)",
                    "16-30 students (KES 16,000 - 30,000 commission)",
                    "30+ students (Cohort Mobilizer)"
                  ]}
                  required
                />
                <InputBlock label="Recruited By (Existing Agent Code)" placeholder="e.g. DIS-PETER104 (Optional)" value={referredByAgent} onChange={e => setReferredByAgent(e.target.value)} />
              </div>
            </div>

            {/* Step 4: Commission & Payout Settlement */}
            <div>
              <FormSectionHeader 
                step="4" 
                title="Commission Terms &amp; Settlement Routing" 
                subtitle="Custom Rep Code assignment, commission percentage, and M-Pesa/Bank settlement"
              />
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <InputBlock label="Custom Rep Code (Auto if blank)" placeholder="e.g. DIS-OMONDI01" value={customCode} onChange={e => setCustomCode(e.target.value.toUpperCase())} />
                <InputBlock label="Commission Rate (%)" type="number" placeholder="10" value={commissionRate} onChange={e => setCommissionRate(e.target.value)} required />
                <InputBlock label="Payout M-Pesa Number" type="tel" placeholder="+254 7XX XXX XXX" value={payoutMpesa} onChange={e => setPayoutMpesa(e.target.value)} required />
                <SelectBlock
                  label="Disbursement Schedule"
                  value={payoutFrequency}
                  onChange={e => setPayoutFrequency(e.target.value)}
                  options={["Weekly (Every Friday)", "Monthly (Last Day of Month)"]}
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <InputBlock label="Settlement Bank Name (Optional)" placeholder="e.g. KCB / Equity / NCBA" value={bankName} onChange={e => setBankName(e.target.value)} />
                <InputBlock label="Bank Account / IBAN" placeholder="e.g. 120039281928" value={bankAccount} onChange={e => setBankAccount(e.target.value)} />
                <InputBlock label="KRA PIN (For Tax Withholding)" placeholder="e.g. A019283748B" value={kraPin} onChange={e => setKraPin(e.target.value)} />
              </div>
            </div>

            {/* Step 5: Verification & Governance */}
            <div>
              <FormSectionHeader 
                step="5" 
                title="KYC Verification &amp; Activation Status" 
                subtitle="ID documentation links, activation fee settlement, and initial status"
              />
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <InputBlock label="National ID Scan Document URL" placeholder="https://drive.google.com/..." value={docRepIdUrl} onChange={e => setDocRepIdUrl(e.target.value)} />
                <InputBlock label="Headshot Photo URL" placeholder="https://..." value={docHeadshotUrl} onChange={e => setDocHeadshotUrl(e.target.value)} />
                <InputBlock label="KES 1,000 Activation Ref (M-Pesa)" placeholder="e.g. QK89284928" value={activationRef} onChange={e => setActivationRef(e.target.value)} />
                <SelectBlock
                  label="Representative Status"
                  value={repStatus}
                  onChange={e => setRepStatus(e.target.value)}
                  options={[
                    { value: 'active', label: 'Active & Verified' },
                    { value: 'pending', label: 'Pending Review' },
                    { value: 'suspended', label: 'Suspended' }
                  ]}
                  required
                />
              </div>
            </div>

            {/* Submission Bar */}
            <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
              <div>
                {statusMessage === 'success' && (
                  <span className="flex items-center gap-2 text-emerald-400 text-xs font-black uppercase tracking-wider">
                    <CheckCircle2 size={16} /> Sales Agent Registered Successfully with Full Schema!
                  </span>
                )}
                {statusMessage === 'saving' && (
                  <span className="text-amber-400 text-xs font-black uppercase tracking-wider flex items-center gap-2">
                    <RefreshCw size={14} className="animate-spin" /> Committing to Cloud Firestore...
                  </span>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="px-6 py-2.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 text-xs font-bold uppercase tracking-wider rounded-xl transition-colors border border-zinc-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={statusMessage === 'saving'}
                  className="flex items-center gap-2 px-8 py-3 bg-[#8B1A2D] hover:bg-[#a61f37] text-white text-xs font-black uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-[#8B1A2D]/20 cursor-pointer disabled:opacity-50"
                >
                  <Plus size={14} /> Commit &amp; Issue Rep Code
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Spreadsheet Master Table Container */}
      <div className="bg-zinc-950 border border-zinc-800 rounded-3xl p-6 shadow-2xl space-y-4">
        {/* Table Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-zinc-800 pb-4">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-xl">
              <BadgePercent size={18} />
            </span>
            <div>
              <h2 className="text-sm font-black text-white uppercase tracking-wider">
                Authorized Field Reps Ledger ({filteredReps.length} of {reps.length})
              </h2>
              <p className="text-[10px] text-zinc-500">Google Sheets-grade spreadsheet with pinned primary identifiers</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative min-w-[220px]">
              <Search className="absolute left-3 top-2.5 text-zinc-500" size={14} />
              <input
                type="text"
                placeholder="Search Code, Name, Phone, Territory..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs rounded-xl pl-9 pr-4 py-2 focus:outline-none focus:border-[#8B1A2D] placeholder-zinc-500"
              />
            </div>

            {/* Territory Filter */}
            <select
              value={territoryFilter}
              onChange={(e) => setTerritoryFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-[#8B1A2D]"
            >
              <option value="all">All Territories</option>
              {territories.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 text-zinc-300 text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-[#8B1A2D]"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active Only</option>
              <option value="pending">Pending Only</option>
              <option value="suspended">Suspended</option>
            </select>

            {/* CSV Export */}
            <button
              onClick={exportToCSV}
              className="flex items-center gap-1.5 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 border border-zinc-800 rounded-xl text-xs font-bold transition-colors"
              title="Export to CSV"
            >
              <Download size={13} /> Export CSV
            </button>

            {/* Refresh */}
            <button
              onClick={fetchRepsAndStudents}
              className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors"
              title="Refresh Directory"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {/* Master Freeze-Pane Table */}
        {loading ? (
          <div className="py-20 text-center text-zinc-500 font-bold text-xs uppercase tracking-widest animate-pulse">
            Loading Commercial Sales Ledger...
          </div>
        ) : filteredReps.length === 0 ? (
          <div className="py-20 text-center text-zinc-500 font-bold text-xs uppercase tracking-widest">
            No sales representatives match your active filters.
          </div>
        ) : (
          <div className="overflow-x-auto border border-zinc-800/80 rounded-2xl">
            <table className="w-full text-left border-collapse min-w-[1700px]">
              <thead>
                <tr className="bg-zinc-900/90 border-b border-zinc-800 text-[9px] font-black uppercase tracking-widest text-zinc-400">
                  {/* Sticky Column 0: Index */}
                  <th className="py-3 px-3 text-center w-12 sticky left-0 z-30 bg-zinc-900 border-r border-zinc-800">
                    #
                  </th>
                  {/* Sticky Column 1: Rep Code & Agent Name */}
                  <th className="py-3 px-4 min-w-[220px] sticky left-12 z-30 bg-zinc-900 border-r border-zinc-800 shadow-[2px_0_5px_rgba(0,0,0,0.5)]">
                    Agent Name &amp; Rep Code
                  </th>
                  <th className="py-3 px-4">Contact Phone</th>
                  <th className="py-3 px-4">Email Address</th>
                  <th className="py-3 px-4">National ID</th>
                  <th className="py-3 px-4">Territory / County</th>
                  <th className="py-3 px-4">Experience &amp; Network</th>
                  <th className="py-3 px-4">Outreach Channel</th>
                  <th className="py-3 px-4">Monthly Target</th>
                  <th className="py-3 px-4 text-center">Comm. Rate</th>
                  <th className="py-3 px-4 text-center">Enrolled Scholars</th>
                  <th className="py-3 px-4 text-right">Gross Sales KES</th>
                  <th className="py-3 px-4 text-right">Commission Accrued</th>
                  <th className="py-3 px-4">Settlement M-Pesa</th>
                  <th className="py-3 px-4">Payout Frequency</th>
                  <th className="py-3 px-4">Activation Fee Ref</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60 text-xs font-medium">
                {filteredReps.map((rep, idx) => {
                  const metrics = getRepMetrics(rep.repCode);
                  const commissionEarned = (metrics.totalGrossSales * (rep.commissionRate || 10)) / 100;

                  return (
                    <tr 
                      key={rep.id || rep.repCode || idx}
                      onClick={() => setSelectedAgent({ ...rep, matchedStudents: metrics.matchedStudents })}
                      className="hover:bg-zinc-900/60 transition-colors cursor-pointer group"
                    >
                      {/* Sticky 0: Index */}
                      <td className="py-3.5 px-3 text-center text-zinc-500 font-mono sticky left-0 z-20 bg-zinc-950 group-hover:bg-zinc-900 border-r border-zinc-800/80">
                        {idx + 1}
                      </td>

                      {/* Sticky 1: Rep Code & Name */}
                      <td className="py-3.5 px-4 sticky left-12 z-20 bg-zinc-950 group-hover:bg-zinc-900 border-r border-zinc-800/80 shadow-[2px_0_5px_rgba(0,0,0,0.5)]">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="font-bold text-white group-hover:text-[#8B1A2D] transition-colors">
                              {rep.name}
                            </div>
                            <div className="text-[10px] font-mono text-amber-400 font-bold flex items-center gap-1.5 mt-0.5">
                              <span>{rep.repCode}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); copyToClipboard(rep.repCode); }}
                                className="text-zinc-600 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Copy Code"
                              >
                                <Copy size={11} />
                              </button>
                            </div>
                          </div>
                          <Eye size={14} className="text-zinc-600 group-hover:text-amber-400 transition-colors" />
                        </div>
                      </td>

                      {/* Contact Phone */}
                      <td className="py-3.5 px-4 font-mono text-zinc-300">
                        {rep.phone || 'N/A'}
                      </td>

                      {/* Email */}
                      <td className="py-3.5 px-4 text-zinc-400 truncate max-w-[180px]">
                        {rep.email || 'N/A'}
                      </td>

                      {/* National ID */}
                      <td className="py-3.5 px-4 font-mono text-zinc-400">
                        {rep.nationalId || 'N/A'}
                      </td>

                      {/* Territory */}
                      <td className="py-3.5 px-4 text-zinc-300">
                        <span className="font-bold">{rep.primaryTerritory || rep.county || 'All Kenya'}</span>
                        {rep.secondaryTerritories && (
                          <div className="text-[10px] text-zinc-500 truncate max-w-[150px]">{rep.secondaryTerritories}</div>
                        )}
                      </td>

                      {/* Experience & Network */}
                      <td className="py-3.5 px-4 text-zinc-400">
                        <div>{rep.salesExperience || '1-2 years'}</div>
                        <div className="text-[10px] text-zinc-500">{rep.networkSize || '200-500 reach'}</div>
                      </td>

                      {/* Outreach Channel */}
                      <td className="py-3.5 px-4 text-zinc-300 truncate max-w-[160px]">
                        {rep.operatingChannels || 'Field Outreach'}
                      </td>

                      {/* Monthly Target */}
                      <td className="py-3.5 px-4 text-emerald-400 font-medium">
                        {rep.monthlyEnrollmentTarget || '6-15 scholars'}
                      </td>

                      {/* Comm. Rate */}
                      <td className="py-3.5 px-4 text-center font-mono font-bold text-amber-400">
                        {rep.commissionRate || 10}%
                      </td>

                      {/* Enrolled Scholars */}
                      <td className="py-3.5 px-4 text-center">
                        <span className="px-3 py-1 bg-zinc-900 border border-zinc-800 text-white group-hover:border-amber-500/40 font-mono font-bold rounded-full transition-colors">
                          {metrics.totalStudents} Scholars
                        </span>
                      </td>

                      {/* Gross Sales */}
                      <td className="py-3.5 px-4 text-right font-mono text-zinc-400">
                        KES {metrics.totalGrossSales.toLocaleString()}
                      </td>

                      {/* Commission Accrued */}
                      <td className="py-3.5 px-4 text-right font-mono font-black text-emerald-400">
                        KES {commissionEarned.toLocaleString()}
                      </td>

                      {/* Settlement M-Pesa */}
                      <td className="py-3.5 px-4 font-mono text-zinc-300">
                        {rep.payoutMpesa || rep.phone || 'Direct Phone'}
                      </td>

                      {/* Payout Frequency */}
                      <td className="py-3.5 px-4 text-zinc-400 text-[11px]">
                        {rep.payoutFrequency || 'Weekly (Friday)'}
                      </td>

                      {/* Activation Fee Ref */}
                      <td className="py-3.5 px-4 font-mono text-[11px] text-amber-300">
                        {rep.activationRef || 'ADMIN-ISSUED'}
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4 text-center">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${
                          (rep.status || 'active') === 'active'
                            ? 'bg-emerald-950/80 text-emerald-400 border border-emerald-800'
                            : 'bg-zinc-800 text-zinc-400'
                        }`}>
                          {rep.status || 'active'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setSelectedAgent({ ...rep, matchedStudents: metrics.matchedStudents }); 
                            }}
                            className="p-1.5 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-lg transition-colors"
                            title="Inspect Agent Ledger"
                          >
                            <Eye size={13} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteRep(rep.repCode); }}
                            className="p-1.5 text-zinc-500 hover:text-red-400 bg-zinc-900 border border-zinc-800 rounded-lg transition-colors"
                            title="Revoke Rep Credentials"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Slide-over Detailed Dossier Drawer */}
      {selectedAgent && (
        <AgentCMSPanel
          agent={selectedAgent}
          students={selectedAgent.matchedStudents || []}
          onClose={() => setSelectedAgent(null)}
        />
      )}
    </div>
  );
}
